/**
 * BulkAcceptModal - Parallel payload creation for bulk transfer acceptance
 *
 * Creates ALL Xaman payloads upfront so the user gets all push notifications
 * at once in Xaman and can sign them back-to-back without returning to the UI.
 * Each payload is monitored independently via its own PayloadWatcher component.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Progress } from '../../ui/components/Progress';
import { Avatar } from '../../ui/components/Avatar';
import { FeatherCheck, FeatherX, FeatherAlertCircle } from '@subframe/core';
import { useXamanPayloadStatus } from '../../hooks/useXamanPayloadStatus';
import type { XamanPayloadDetails } from '../transactions/XamanSigningModal';
import { WalletSigningModal } from '../transactions/WalletSigningModal';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { getWalletProvider, toSigningPayload } from '../../lib/wallet';
import type { SigningPayload } from '../../types/signing';
import type { WalletOffer } from '../../types/wallet';

/** Small inline spinner SVG — symmetric, no wobble */
const Spinner: React.FC<{ className?: string }> = ({ className = 'h-4 w-4' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

interface BulkAcceptModalProps {
  open: boolean;
  onClose: () => void;
  offers: WalletOffer[];
  collectionName: string;
  collectionImage: string | null;
  onOfferAccepted: (offerId: string) => void;
  onComplete: () => void;
}

type BulkStatus = 'idle' | 'creating-payloads' | 'signing' | 'completed' | 'error';

type OfferItemStatus = 'pending' | 'creating' | 'ready' | 'signing' | 'accepted' | 'failed' | 'create-failed';

interface OfferItem {
  offerId: string;
  nftTokenId: string;
  nftName: string | null;
  nftImage: string | null;
  status: OfferItemStatus;
  payload: XamanPayloadDetails | null;
}

/**
 * PayloadWatcher — monitors a single Xaman payload's signing status.
 * Renders nothing visible; just uses the hook and calls back on status change.
 */
const PayloadWatcher: React.FC<{
  payload: XamanPayloadDetails;
  offerId: string;
  onSigned: (offerId: string, txHash: string) => void;
  onRejected: (offerId: string) => void;
}> = ({ payload, offerId, onSigned, onRejected }) => {
  const calledRef = useRef(false);

  useXamanPayloadStatus({
    uuid: payload.uuid,
    websocketUrl: payload.websocketUrl,
    onSigned: (txHash) => {
      if (!calledRef.current) {
        calledRef.current = true;
        onSigned(offerId, txHash);
      }
    },
    onRejected: () => {
      if (!calledRef.current) {
        calledRef.current = true;
        onRejected(offerId);
      }
    },
    onExpired: () => {
      if (!calledRef.current) {
        calledRef.current = true;
        onRejected(offerId);
      }
    },
  });

  return null;
};

export const BulkAcceptModal: React.FC<BulkAcceptModalProps> = ({
  open,
  onClose,
  offers,
  collectionName,
  collectionImage,
  onOfferAccepted,
  onComplete,
}) => {
  const { user, walletProvider } = useAuthStore();
  const [bulkStatus, setBulkStatus] = useState<BulkStatus>('idle');
  const [items, setItems] = useState<OfferItem[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState(0);
  const hasStartedRef = useRef(false);

  // Client-signing wallets (joey-wc) sign over a single WalletConnect session and can't
  // hold many concurrent requests like Xaman's push queue — so they sign one at a time.
  const isClientSigning = getWalletProvider(walletProvider).capabilities.clientSigning;
  const [currentSigning, setCurrentSigning] = useState<SigningPayload | null>(null);
  const signResolverRef = useRef<((outcome: 'signed' | 'rejected') => void) | null>(null);

  // Initialize when modal opens
  useEffect(() => {
    if (open && offers.length > 0) {
      setItems(
        offers.map((offer) => ({
          offerId: offer.offerId,
          nftTokenId: offer.nftTokenId,
          nftName: offer.nftName,
          nftImage: offer.nftImage,
          status: 'pending',
          payload: null,
        }))
      );
      setBulkStatus('idle');
      setErrorMessage(null);
      setCreatedCount(0);
      setCurrentSigning(null);
      signResolverRef.current = null;
      hasStartedRef.current = false;
    }
  }, [open, offers]);

  const totalCount = items.length;
  const acceptedCount = items.filter((s) => s.status === 'accepted').length;
  const failedCount = items.filter((s) => s.status === 'failed' || s.status === 'create-failed').length;
  const readyItems = items.filter((s) => s.payload !== null);
  const isFinished = bulkStatus === 'completed' || bulkStatus === 'error';

  // Check if all items are resolved (accepted or failed)
  useEffect(() => {
    if (bulkStatus !== 'signing') return;
    const allResolved = items.every(
      (i) => i.status === 'accepted' || i.status === 'failed' || i.status === 'create-failed'
    );
    if (allResolved) {
      setBulkStatus('completed');
    }
  }, [items, bulkStatus]);

  const createAllPayloads = useCallback(async () => {
    if (!user?.xrpl_address || hasStartedRef.current) return;
    hasStartedRef.current = true;
    setBulkStatus('creating-payloads');
    setCreatedCount(0);

    // Create all payloads with limited concurrency to avoid hammering the API
    const CONCURRENCY = 3;
    const offersCopy = [...offers];
    let completed = 0;

    const createOne = async (offer: WalletOffer, index: number) => {
      setItems((prev) =>
        prev.map((item, i) => (i === index ? { ...item, status: 'creating' } : item))
      );

      try {
        const response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: offer.nftTokenId,
          offerIndex: offer.offerId,
          userAddress: user.xrpl_address,
          offerType: 'sell',
        });

        if (response.success && response.data?.payload) {
          const payload: XamanPayloadDetails = {
            uuid: response.data.payload.uuid,
            qrUrl: response.data.payload.qrUrl,
            deepLink: response.data.payload.deepLink,
            websocketUrl: response.data.payload.websocketUrl,
          };
          setItems((prev) =>
            prev.map((item, i) =>
              i === index ? { ...item, status: 'ready', payload } : item
            )
          );
        } else {
          throw new Error(response.error || 'Failed to create payload');
        }
        // NOTE: the parallel path is Xaman-only (it relies on Xaman push + websocket
        // status). joey-wc goes through runSequential() instead.
      } catch (err) {
        setItems((prev) =>
          prev.map((item, i) =>
            i === index ? { ...item, status: 'create-failed' } : item
          )
        );
      }

      completed++;
      setCreatedCount(completed);
    };

    // Process in batches of CONCURRENCY
    for (let i = 0; i < offersCopy.length; i += CONCURRENCY) {
      const batch = offersCopy.slice(i, i + CONCURRENCY);
      await Promise.all(
        batch.map((offer, batchIdx) => createOne(offer, i + batchIdx))
      );
    }

    // All payloads created — switch to signing mode
    setBulkStatus('signing');
    // Mark all 'ready' items as 'signing' since watchers are now active
    setItems((prev) =>
      prev.map((item) =>
        item.status === 'ready' ? { ...item, status: 'signing' } : item
      )
    );
  }, [offers, user?.xrpl_address]);

  /**
   * Sequential signing for client-signing wallets (joey-wc): create one payload, sign it
   * via WalletSigningModal, wait for the outcome, then move to the next. WalletConnect
   * permits only one in-flight request, so the all-at-once Xaman path doesn't apply.
   */
  const runSequential = useCallback(async () => {
    if (!user?.xrpl_address || hasStartedRef.current) return;
    hasStartedRef.current = true;
    setBulkStatus('signing');

    for (let index = 0; index < offers.length; index++) {
      const offer = offers[index];
      setItems((prev) => prev.map((it, i) => (i === index ? { ...it, status: 'creating' } : it)));

      let normalized: SigningPayload;
      try {
        const response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: offer.nftTokenId,
          offerIndex: offer.offerId,
          userAddress: user.xrpl_address,
          offerType: 'sell',
        });
        const raw = response.data?.signingIntent ?? response.data?.payload;
        if (!response.success || !raw) throw new Error(response.error || 'Failed to create payload');
        normalized = toSigningPayload(raw);
      } catch {
        setItems((prev) => prev.map((it, i) => (i === index ? { ...it, status: 'create-failed' } : it)));
        continue;
      }

      setItems((prev) => prev.map((it, i) => (i === index ? { ...it, status: 'signing' } : it)));

      // Surface the signing modal and block until the user resolves it.
      const outcome = await new Promise<'signed' | 'rejected'>((resolve) => {
        signResolverRef.current = resolve;
        setCurrentSigning(normalized);
      });
      signResolverRef.current = null;
      setCurrentSigning(null);

      if (outcome === 'signed') {
        setItems((prev) => prev.map((it, i) => (i === index ? { ...it, status: 'accepted' } : it)));
        apiClient.post('/offers/transfer/confirm', { offerId: offer.offerId, action: 'accepted' }).catch(() => {});
        onOfferAccepted(offer.offerId);
      } else {
        setItems((prev) => prev.map((it, i) => (i === index ? { ...it, status: 'failed' } : it)));
      }
    }

    setBulkStatus('completed');
  }, [offers, user?.xrpl_address, onOfferAccepted]);

  const handlePayloadSigned = useCallback(
    (offerId: string, txHash: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.offerId === offerId ? { ...item, status: 'accepted' } : item
        )
      );

      // Confirm the transfer in backend
      apiClient
        .post('/offers/transfer/confirm', { offerId, action: 'accepted' })
        .catch(() => {});

      // Notify parent to optimistically remove
      onOfferAccepted(offerId);
    },
    [onOfferAccepted]
  );

  const handlePayloadRejected = useCallback((offerId: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.offerId === offerId ? { ...item, status: 'failed' } : item
      )
    );
  }, []);

  const handleClose = useCallback(() => {
    if (acceptedCount > 0) {
      onComplete();
    }
    onClose();
  }, [acceptedCount, onComplete, onClose]);

  // Find the first ready/signing payload to show QR for (fallback for non-push)
  const firstPendingItem = items.find((i) => i.status === 'signing' && i.payload);

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <Dialog.Content className="w-full max-w-[460px]">
        {/* Header */}
        <div className="flex w-full items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar size="medium" image={collectionImage || undefined} square>
              {collectionName?.[0] || 'C'}
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Accept Transfers
              </span>
              <span className="text-body font-body text-subtext-color truncate">
                {collectionName}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded hover:opacity-70 transition-opacity"
            aria-label="Close"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>
        <div className="w-full h-px bg-neutral-border" />

        {/* Content */}
        <div className="flex w-full flex-col items-stretch gap-5 px-6 py-5">
          {/* Idle state — confirmation before starting */}
          {bulkStatus === 'idle' && (
            <div className="flex flex-col items-center gap-3 w-full py-2">
              <span className="text-body font-body text-subtext-color text-center">
                {isClientSigning ? (
                  <>
                    You'll approve {totalCount} transaction{totalCount !== 1 ? 's' : ''} in your
                    wallet, one at a time.
                  </>
                ) : (
                  <>
                    This will send {totalCount} signing request{totalCount !== 1 ? 's' : ''} to your
                    Xaman wallet. You can sign them all back-to-back in Xaman.
                  </>
                )}
              </span>
            </div>
          )}

          {/* Creating payloads progress */}
          {bulkStatus === 'creating-payloads' && (
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="flex items-center gap-2">
                <Spinner className="h-5 w-5 text-brand-primary" />
                <span className="text-body font-body text-subtext-color">
                  Preparing transactions... {createdCount}/{totalCount}
                </span>
              </div>
              <Progress value={(createdCount / totalCount) * 100} />
            </div>
          )}

          {/* Signing progress */}
          {(bulkStatus === 'signing' || isFinished) && (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex justify-between text-body font-body text-subtext-color">
                <span>
                  {isFinished
                    ? `${acceptedCount} of ${totalCount} accepted`
                    : `${acceptedCount} of ${totalCount} signed`}
                </span>
                <span>{Math.round((acceptedCount / totalCount) * 100)}%</span>
              </div>
              <Progress value={(acceptedCount / totalCount) * 100} />
            </div>
          )}

          {/* Open in Xaman prompt — shown while signing, with QR fallback */}
          {bulkStatus === 'signing' && firstPendingItem?.payload && (
            <div className="flex flex-col items-center gap-4 w-full">
              <span className="text-body font-body text-subtext-color text-center">
                Open Xaman to sign your transactions
              </span>
              <img
                src={firstPendingItem.payload.qrUrl}
                alt="Xaman QR Code"
                style={{ width: 200, height: 200 }}
                className="rounded-md"
              />
              <Button
                variant="neutral-secondary"
                size="medium"
                onClick={() => window.open(firstPendingItem.payload!.deepLink, '_blank')}
                className="w-full"
              >
                Open in Xaman
              </Button>
            </div>
          )}

          {/* Status list */}
          {bulkStatus !== 'idle' && bulkStatus !== 'creating-payloads' && items.length > 1 && (
            <div className="flex flex-col gap-0.5 w-full max-h-[200px] overflow-y-auto rounded-md border border-solid border-neutral-border p-1">
              {items.map((item) => (
                <div
                  key={item.offerId}
                  className="flex items-center gap-2 py-1.5 px-2 rounded text-caption font-caption"
                >
                  {item.status === 'accepted' && (
                    <FeatherCheck className="h-3.5 w-3.5 text-success-600 flex-shrink-0" />
                  )}
                  {(item.status === 'failed' || item.status === 'create-failed') && (
                    <FeatherX className="h-3.5 w-3.5 text-error-600 flex-shrink-0" />
                  )}
                  {item.status === 'signing' && (
                    <Spinner className="h-3.5 w-3.5 text-brand-primary flex-shrink-0" />
                  )}
                  {(item.status === 'pending' || item.status === 'creating' || item.status === 'ready') && (
                    <span className="h-3.5 w-3.5 flex-shrink-0" />
                  )}
                  <span
                    className={`truncate ${
                      item.status === 'accepted'
                        ? 'text-success-600'
                        : item.status === 'failed' || item.status === 'create-failed'
                          ? 'text-error-600'
                          : item.status === 'signing'
                            ? 'text-default-font'
                            : 'text-subtext-color'
                    }`}
                  >
                    {item.nftName || 'Unnamed NFT'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Error message */}
          {errorMessage && (
            <div className="flex items-center gap-2 p-3 rounded-md border border-solid border-error-600 w-full">
              <FeatherAlertCircle className="h-4 w-4 text-error-600 flex-shrink-0" />
              <span className="text-body font-body text-error-600">{errorMessage}</span>
            </div>
          )}

          {/* Completed summary */}
          {bulkStatus === 'completed' && acceptedCount === totalCount && (
            <div className="flex items-center gap-2 p-3 rounded-md border border-solid border-success-600 w-full">
              <FeatherCheck className="h-4 w-4 text-success-600 flex-shrink-0" />
              <span className="text-body font-body text-success-600">
                All {totalCount} transfers accepted!
              </span>
            </div>
          )}

          {bulkStatus === 'completed' && acceptedCount > 0 && acceptedCount < totalCount && (
            <div className="flex items-center gap-2 p-3 rounded-md border border-solid border-neutral-border w-full">
              <FeatherCheck className="h-4 w-4 text-success-600 flex-shrink-0" />
              <span className="text-body font-body text-subtext-color">
                {acceptedCount} of {totalCount} accepted.
                {failedCount > 0 && ` ${failedCount} failed or rejected.`}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="w-full h-px bg-neutral-border" />
        <div className="flex w-full items-center justify-end gap-3 px-6 py-4">
          {bulkStatus === 'idle' && (
            <>
              <Button variant="neutral-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="brand-primary" onClick={isClientSigning ? runSequential : createAllPayloads}>
                Accept All ({totalCount})
              </Button>
            </>
          )}

          {bulkStatus === 'creating-payloads' && (
            <Button variant="neutral-secondary" disabled>
              Preparing...
            </Button>
          )}

          {bulkStatus === 'signing' && (
            <Button variant="neutral-secondary" onClick={handleClose}>
              Close
            </Button>
          )}

          {isFinished && (
            <Button variant="brand-primary" onClick={handleClose}>
              {acceptedCount > 0 ? `Done (${acceptedCount} accepted)` : 'Close'}
            </Button>
          )}
        </div>

        {/* Invisible payload watchers — one per payload, monitoring signing status */}
        {bulkStatus === 'signing' &&
          items.map(
            (item) =>
              item.payload &&
              (item.status === 'signing' || item.status === 'ready') && (
                <PayloadWatcher
                  key={item.offerId}
                  payload={item.payload}
                  offerId={item.offerId}
                  onSigned={handlePayloadSigned}
                  onRejected={handlePayloadRejected}
                />
              )
          )}
      </Dialog.Content>
    </Dialog>

    {/* Sequential signing surface for client-signing wallets (joey-wc). */}
    {currentSigning && (
      <WalletSigningModal
        open={true}
        onClose={() => signResolverRef.current?.('rejected')}
        title="Accept Transfer"
        payload={currentSigning}
        onSuccess={() => signResolverRef.current?.('signed')}
        onRejected={() => signResolverRef.current?.('rejected')}
        onExpired={() => signResolverRef.current?.('rejected')}
      />
    )}
    </>
  );
};
